import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { USE_MOCK_SHOPS, getMockNearbyCenter, getMockNearbyShops } from '../data/mockShops';

export const CoffeeShopContext = createContext();

const LOCATION_KEY = "kuthe:userLocation";

function readStoredLocation() {
  try {
    const raw = sessionStorage.getItem(LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.latitude);
    const lng = Number(parsed?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      ...parsed,
      latitude: lat,
      longitude: lng,
      ...(parsed.radius != null ? { radius: Number(parsed.radius) } : {}),
    };
  } catch {
    return null;
  }
}

function writeStoredLocation(location) {
  try {
    if (!location) {
      sessionStorage.removeItem(LOCATION_KEY);
      return;
    }
    sessionStorage.setItem(LOCATION_KEY, JSON.stringify(location));
  } catch {
    // Private mode / quota — ignore; in-memory state still works for the session.
  }
}

export const CoffeeShopProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(
    () => (USE_MOCK_SHOPS ? getMockNearbyCenter() : readStoredLocation()),
  );
  // Same split as the mobile apps (coffee-pass context/CoffeeShopContext.js):
  //   userLocation — the search centre. Written by GPS, the search bar, and
  //                  "Search this area". Drives the /places fetch.
  //   gpsLocation  — where the device actually is. Only ever written from a real
  //                  geolocation fix, and only this drives the blue dot.
  // Deliberately not persisted: a dot restored from sessionStorage isn't a
  // real-time position, and we don't hold a location the browser hasn't re-granted.
  const [gpsLocation, setGpsLocation] = useState(null);
  const [coffeeShops, setCoffeeShops] = useState(() => (USE_MOCK_SHOPS ? getMockNearbyShops() : []));
  const [loading, setLoading] = useState(!USE_MOCK_SHOPS);
  const [errorMessage, setErrorMessage] = useState(null);

  const updateUserLocation = (newLocation) => {
    setUserLocation(newLocation);
    writeStoredLocation(newLocation);
  };

  // Mirrors the mobile mount check (getForegroundPermissionsAsync → granted → fix):
  // if access is already granted, put the dot back without prompting. Unlike mobile
  // this does NOT move the search centre — that would spend a Places call on every
  // page load. Only a deliberate "Find near me" or search does that.
  const refreshGpsLocation = useCallback(() => {
    if (USE_MOCK_SHOPS || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => setGpsLocation({ latitude: coords.latitude, longitude: coords.longitude }),
      () => {},
    );
  }, []);

  useEffect(() => {
    // Safari has navigator.permissions but rejects the 'geolocation' name — there
    // the dot waits for an explicit "Find near me" rather than prompting on load.
    try {
      navigator.permissions
        ?.query({ name: 'geolocation' })
        .then(({ state }) => { if (state === 'granted') refreshGpsLocation(); })
        .catch(() => {});
    } catch {
      // permissions API unavailable — same fallback.
    }
  }, [refreshGpsLocation]);

  useEffect(() => {
    if (USE_MOCK_SHOPS) return;

    const fetchData = async () => {
      const baseURL = process.env.REACT_APP_SERVER_BASE_URL || "http://localhost:3001/";
      if (!userLocation) {
        setLoading(false);
        return;
      }

      setLoading(true);
      // Clear previous results so old-city pins don't linger under the new camera
      setCoffeeShops([]);
      try {
        const radiusParam = userLocation.radius ? `&radius=${userLocation.radius}` : '';
        const url = `${baseURL}places?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}${radiusParam}`;
        const response = await axios.get(url);
        setCoffeeShops(Array.isArray(response.data) ? response.data : []);
        setErrorMessage(null);
      } catch (error) {
        const status = error.response?.status;
        if (status === 429) {
          setErrorMessage("Too many searches - please wait a moment and try again.");
        } else if (status >= 500) {
          setErrorMessage("Something went wrong on our end. Please try again shortly.");
        } else {
          setErrorMessage("Couldn't load shops. Please check your connection and try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userLocation]);

  const value = { coffeeShops, loading, userLocation, updateUserLocation, gpsLocation, setGpsLocation, errorMessage};

  return <CoffeeShopContext.Provider value={value}>{children}</CoffeeShopContext.Provider>;
};
